import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { LoaderCircle } from 'lucide-react'
import * as yup from 'yup'
import { Formik, Form, useField } from 'formik'
import { useMutation } from '@apollo/react-hooks'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'

import { Mutation } from '../../api/gql'
import { useErrorMessage } from '../../hooks'
import logo from '../../assets/img/logo_signin.png'
import LanguageSelect from '../../components/LanguageSelect'

/** Thin Formik-bound wrapper around shadcn Input */
const FormInput = ({ name, ...props }: React.ComponentProps<'input'> & { name: string }) => {
  const [field, meta] = useField(name)
  return (
    <div className="flex flex-col gap-1">
      <Input
        {...field}
        {...props}
        className={cn(meta.touched && meta.error && 'border-destructive')}
      />
      {meta.touched && meta.error && (
        <p className="text-xs text-destructive">{meta.error}</p>
      )}
    </div>
  )
}

interface Organization {
  id: string
  name: string
  rights: string
}

const SignIn = ({ history }) => {
  const [initialValues, setInitialValues] = useState({
    email: '',
    password: '',
    remember: false,
  })
  const [pendingOrgs, setPendingOrgs] = useState<Organization[] | null>(null)
  const [pendingCredentials, setPendingCredentials] = useState<{
    email: string
    password: string
    remember: boolean
  } | null>(null)

  const { t } = useTranslation()
  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .email(t('Sign_in_form_validation_email_format'))
      .required(t('Sign_in_form_validation_email_required')),
    password: yup.string().required(t('Sign_in_form_validation_password_required')),
  })

  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') as string)
      if (user?.email) {
        // Legacy builds also stored the password here in plaintext. Strip it,
        // keeping only the remembered email — the password is never persisted.
        if (user.password) localStorage.setItem('user', JSON.stringify({ email: user.email }))
        setInitialValues({ email: user.email, password: '', remember: true })
      }
    } catch (_e) {}
  }, [])

  const [signIn, { error }] = useMutation(Mutation.SIGN_IN)
  const [formError, setFormError] = useErrorMessage(error)
  // Backend errors are English project-wide; the ones a signing-in user can hit
  // get a translation here, everything else falls through verbatim.
  const displayError =
    formError === 'This account has been deactivated.'
      ? t('Sign_in_error_deactivated')
      : formError

  const handleOrgSelect = async (orgId: string) => {
    if (!pendingCredentials) return
    try {
      const { data } = await signIn({
        variables: { ...pendingCredentials, organizationId: orgId },
      })
      const result = data.signIn
      if (result.token) {
        if (pendingCredentials.remember) {
          // Remember the email only — never persist the password.
          localStorage.setItem('user', JSON.stringify({ email: pendingCredentials.email }))
        } else {
          localStorage.removeItem('user')
        }
        window.location.href = '/'
      }
    } catch (e) {
      // error handled by Apollo
    }
  }

  // Organization picker view
  if (pendingOrgs && pendingOrgs.length > 0) {
    return (
      <>
        <div className="flex items-center gap-3 self-center">
          <img src={logo} className="h-36" alt="Urkla" />
        </div>

        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-xl">{t('Sign_in_heading')}</CardTitle>
            <CardDescription>{t('Select_organization')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-2">
              {displayError && (
                <div className="rounded-md bg-destructive/15 px-4 py-3 text-sm text-destructive">
                  {displayError}
                </div>
              )}
              {pendingOrgs.map((org) => (
                <button
                  key={org.id}
                  type="button"
                  className="flex items-center justify-between rounded-lg border bg-background px-4 py-3 text-left transition-colors hover:bg-accent"
                  onClick={() => handleOrgSelect(org.id)}
                >
                  <span className="font-medium">{org.name}</span>
                  <span className="text-sm text-muted-foreground">{org.rights}</span>
                </button>
              ))}
              <Button
                variant="ghost"
                className="mt-2"
                onClick={() => {
                  setPendingOrgs(null)
                  setPendingCredentials(null)
                }}
              >
                {t('Common_back')}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-center">
          <LanguageSelect />
        </div>
      </>
    )
  }

  return (
    <>
      <div className="flex items-center gap-3 self-center">
        <img src={logo} className="h-36" alt="Urkla" />
      </div>

      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">{t('Sign_in_heading')}</CardTitle>
          <CardDescription>{t('Sign_in_subheading')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Formik
            validationSchema={validationSchema}
            initialValues={initialValues}
            enableReinitialize
            validateOnMount={false}
            onSubmit={async (values) => {
              setFormError?.('')
              try {
                const { data } = await signIn({ variables: values })
                const result = data.signIn

                if (result.requiresOrgSelection) {
                  setPendingOrgs(result.organizations)
                  setPendingCredentials(values)
                  return
                }

                window.location.href = '/'

                if (values.remember) {
                  // Remember the email only — never persist the password.
                  localStorage.setItem('user', JSON.stringify({ email: values.email }))
                } else localStorage.removeItem('user')
              } catch (e) {
                // error handled by Apollo
              }
            }}
          >
            {({ isSubmitting, values, setFieldValue }) => (
              <Form>
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="email">{t('Sign_in_form_email_label')}</Label>
                    <FormInput
                      name="email"
                      type="email"
                      id="email"
                      placeholder={t('Sign_in_form_email_placeholder')}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <div className="flex items-center">
                      <Label htmlFor="password">{t('Sign_in_form_password_label')}</Label>
                      <Link
                        to="/auth/reset"
                        className="ml-auto text-sm underline-offset-4 hover:underline"
                      >
                        {t('Sign_up_form_forgot_password_link')}
                      </Link>
                    </div>
                    <FormInput
                      name="password"
                      type="password"
                      id="password"
                      placeholder={t('Sign_in_form_password_placeholder')}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="remember"
                      checked={values.remember}
                      onCheckedChange={(checked) => setFieldValue('remember', checked)}
                    />
                    <Label htmlFor="remember" className="text-sm font-normal">
                      {t('Sign_in_form_remember_label')}
                    </Label>
                  </div>

                  {displayError && (
                    <div className="rounded-md bg-destructive/15 px-4 py-3 text-sm text-destructive">
                      {displayError}
                    </div>
                  )}

                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting && <LoaderCircle className="animate-spin" />}
                    {t('Sign_in_form_sign_in_btn_label')}
                  </Button>

                  <p className="text-center text-sm text-muted-foreground">
                    {t('Sign_in_no_account_text', "Don't have an account?")}{' '}
                    <Link
                      to="/auth/signup"
                      className="text-primary underline-offset-4 hover:underline"
                    >
                      {t('Sign_in_form_sign_up_link')}
                    </Link>
                  </p>
                </div>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>

      <div className="flex justify-center">
        <LanguageSelect />
      </div>
    </>
  )
}

export default SignIn
